import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useRef, useState } from "react";
import { rundenStellenNachZahlenraum, type RundenExerciseConfig, type RundenRunSettings, type RundenStelle } from "@calega/content";
import type { RundenRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "feedback" | "complete";
interface RundenAnswer { itemId: string; value: number; expected: number; correct: boolean }
interface State {
  runId: string;
  run: RundenRun;
  index: number;
  phase: Phase;
  value: string;
  answers: RundenAnswer[];
  feedback: RundenAnswer | null;
  endsAt: number | null;
  result: { score: number; maxScore: number } | null;
}

const ranges: Array<{ id: RundenRunSettings["range"]; label: string }> = [
  { id: "0-100", label: "0–100" },
  { id: "100-1000", label: "100–1.000" },
  { id: "1000-1000000", label: "1.000–1.000.000" },
];

const places: Array<{ id: RundenStelle; label: string }> = [
  { id: "ones", label: "Einerstelle" },
  { id: "tens", label: "Zehnerstelle" },
  { id: "hundreds", label: "Hunderterstelle" },
  { id: "thousands", label: "Tausenderstelle" },
  { id: "ten-thousands", label: "Zehntausenderstelle" },
  { id: "hundred-thousands", label: "Hunderttausenderstelle" },
];

function allowedPlaces(range: RundenRunSettings["range"], decimals: boolean) {
  return rundenStellenNachZahlenraum[range].filter((place) => decimals || place !== "ones");
}

function placeLabel(place: RundenStelle) {
  return places.find(({ id }) => id === place)?.label ?? place;
}

function formatNumber(value: number) {
  return value.toLocaleString("de-DE", { maximumFractionDigits: 3 });
}

function parseNumber(value: string) {
  const normalized = value.trim().replace(",", ".");
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1_000_000 ? parsed : null;
}

export function rundenAdvancesAutomatically(inSyncSession: boolean) {
  return !inSyncSession;
}

export function Runden({ account, config, pageId }: { account: Account; config: RundenExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const inSyncSession = Boolean(realtime.session);
  const follower = inSyncSession && !facilitator;
  const canControl = !inSyncSession || facilitator;
  const canAnswer = !realtime.session || facilitator || follower;
  const [mode, setMode] = useState<RundenRunSettings["mode"]>(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [range, setRange] = useState<RundenRunSettings["range"]>(config.defaultRange);
  const [decimals, setDecimals] = useState(config.defaultDecimals);
  const [selectedPlaces, setSelectedPlaces] = useState<RundenStelle[]>(config.defaultPlaces);
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [, tick] = useState(0);
  const answerRef = useRef<HTMLInputElement>(null);
  const correctCount = useMemo(() => exercise?.answers.filter(({ correct }) => correct).length ?? 0, [exercise?.answers]);

  function publish(state: State | null) {
    realtime.publishExercise({ exerciseType: "runden", pageId, state });
  }

  useEffect(() => {
    type SyncedState = Omit<State, "value"> & Partial<Pick<State, "value">>;
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: SyncedState | null } | null;
    if (synced?.exerciseType === "runden" && synced.pageId === pageId) {
      setExercise(synced.state ? { ...synced.state, value: synced.state.value ?? "" } : null);
    }
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (exercise?.phase === "running") answerRef.current?.focus();
  }, [exercise?.phase, exercise?.index]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const interval = window.setInterval(() => tick((current) => current + 1), 250);
    return () => window.clearInterval(interval);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const timeout = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow)) + 20);
    return () => clearExerciseTimeout(timeout);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, canControl, busy]);

  useEffect(() => {
    if (exercise?.phase !== "feedback" || !canControl || !rundenAdvancesAutomatically(inSyncSession)) return;
    const timeout = setExerciseTimeout(() => advance().catch((error) => setMessage(error.message)), exercise.feedback?.correct ? 650 : 1_250);
    return () => clearExerciseTimeout(timeout);
  }, [exercise?.phase, exercise?.index, exercise?.feedback?.correct, canControl, inSyncSession, busy]);

  function settings(): RundenRunSettings {
    return { mode, count, durationSeconds, range, places: selectedPlaces, decimals };
  }

  function keepValidPlaces(nextRange: RundenRunSettings["range"], nextDecimals: boolean) {
    const allowed = allowedPlaces(nextRange, nextDecimals);
    setSelectedPlaces((current) => {
      const filtered = current.filter((place) => allowed.includes(place));
      return filtered.length ? filtered : [allowed[0]!];
    });
  }

  function changeRange(nextRange: RundenRunSettings["range"]) {
    setRange(nextRange);
    keepValidPlaces(nextRange, decimals);
  }

  function changeDecimals(nextDecimals: boolean) {
    setDecimals(nextDecimals);
    keepValidPlaces(range, nextDecimals);
  }

  function togglePlace(place: RundenStelle) {
    setSelectedPlaces((current) => current.includes(place)
      ? current.length === 1 ? current : current.filter((item) => item !== place)
      : [...current, place]);
  }

  async function start(runSettings = settings()) {
    if (busy || !canControl || !runSettings.places.length) return;
    setBusy(true);
    try {
      const response = await startExerciseRun<{ runId: string; exercise: RundenRun }>({ contentPageId: pageId, settings: runSettings });
      const next: State = { runId: response.runId, run: response.exercise, index: 0, phase: "running", value: "", answers: [], feedback: null, endsAt: response.exercise.settings.mode === "time" ? exerciseNow(realtime.serverNow) + response.exercise.settings.durationSeconds * 1_000 : null, result: null };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally { setBusy(false); }
  }

  async function abort() {
    if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
  }

  async function back() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abort();
      setMode(exercise.run.settings.mode);
      setCount(exercise.run.settings.count);
      setDurationSeconds(exercise.run.settings.durationSeconds);
      setRange(exercise.run.settings.range);
      setSelectedPlaces(exercise.run.settings.places);
      setDecimals(exercise.run.settings.decimals);
      setExercise(null);
      publish(null);
    } finally { setBusy(false); }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const runSettings = exercise.run.settings;
    setBusy(true);
    try { await abort(); } finally { setBusy(false); }
    await start(runSettings);
  }

  async function check() {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    const parsed = parseNumber(exercise.value);
    if (!item || parsed === null) {
      setMessage("Gib eine gültige Zahl ein.");
      return;
    }
    setBusy(true);
    try {
      await submitExerciseAnswer(exercise.runId, { type: "runden", itemId: item.id, value: parsed });
      const feedback: RundenAnswer = { itemId: item.id, value: parsed, expected: item.result, correct: parsed === item.result };
      const next = { ...exercise, phase: "feedback" as const, answers: [...exercise.answers, feedback], feedback };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally { setBusy(false); }
  }

  async function advance() {
    if (!exercise || busy || !canControl || exercise.phase === "complete") return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) return finish();
    const next = { ...exercise, index: exercise.index + 1, phase: "running" as const, value: "", feedback: null };
    setExercise(next);
    publish(next);
  }

  function updateValue(value: string) {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const next = { ...exercise, value };
    setExercise(next);
    publish(next);
  }

  async function skip() {
    if (!exercise || exercise.phase !== "running" || busy || !canControl) return;
    await advance();
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next = { ...exercise, result, phase: "complete" as const };
      setExercise(next);
      publish(next);
      setMessage("");
    });
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await back();
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) return <ExerciseWaitingCard className="mathe-exercise" />;

  if (!exercise) {
    const availablePlaces = allowedPlaces(range, decimals);
    return <section className="mathe-exercise">
      <p className="quiz-instructions learning-text">{config.instructions}</p>
      <div className="mathe-settings rounding-settings">
        <fieldset><legend>Zahlenraum</legend><div className="mathe-option-grid">{ranges.map((item) => <label className="checkbox-label" key={item.id}><input type="radio" name={`${pageId}-rounding-range`} checked={range === item.id} onChange={() => changeRange(item.id)} />{item.label}</label>)}</div></fieldset>
        <fieldset><legend>Zahlenart</legend><label className="checkbox-label"><input type="checkbox" checked={decimals} onChange={(event) => changeDecimals(event.target.checked)} />Dezimalzahlen mit einer Nachkommastelle</label><p className="field-help">Die Einerstelle wird bei Dezimalzahlen verfügbar.</p></fieldset>
        <fieldset><legend>Rundungsstellen</legend><div className="mathe-option-grid">{availablePlaces.map((place) => <label className="checkbox-label" key={place}><input type="checkbox" checked={selectedPlaces.includes(place)} onChange={() => togglePlace(place)} />{placeLabel(place)}</label>)}</div></fieldset>
        <fieldset><legend>Durchlauf</legend><div className="mathe-run-settings"><div className="blitz-choice-row"><label className="checkbox-label"><input type="radio" name={`${pageId}-rounding-mode`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label><label className="checkbox-label"><input type="radio" name={`${pageId}-rounding-mode`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label></div>{mode === "count" ? <label>Anzahl Aufgaben<select value={count} onChange={(event) => setCount(Number(event.target.value))}>{[5, 10, 15, 20].map((item) => <option key={item}>{item}</option>)}</select></label> : <ExerciseDurationSelect value={durationSeconds} onChange={setDurationSeconds} />}</div></fieldset>
      </div>
      <button className="primary" disabled={busy || !canControl || !selectedPlaces.length} onClick={() => start().catch((error) => setMessage(error.message))}>Rundungsaufgaben starten</button>
      {message && <div className="alert info">{message}</div>}
    </section>;
  }

  const item = exercise.run.items[exercise.index];
  const remaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  const last = (exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length;
  return <section className="mathe-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu><button className="secondary small" disabled={busy} onClick={() => back().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button><button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button></ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${exercise.answers.length} Aufgaben beantwortet · ${remaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete" ? <><div className="reime-result"><strong>{exercise.result?.score ?? correctCount} von {exercise.result?.maxScore ?? exercise.answers.length} Aufgaben richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div>{canControl && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}{exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}</> : item && <>
      <div className="mathe-card">
        <div className="rounding-question"><span>Runde <strong>{formatNumber(item.value)}</strong> auf die nächstgelegene <strong>{placeLabel(item.place)}</strong>.</span><input ref={answerRef} className={exercise.phase === "feedback" ? exercise.feedback?.correct ? "correct" : "wrong" : ""} type="text" inputMode="decimal" value={exercise.phase === "feedback" && exercise.feedback ? formatNumber(exercise.feedback.value) : exercise.value} disabled={exercise.phase !== "running" || busy || !canAnswer} onChange={(event) => updateValue(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") check().catch((error) => setMessage(error.message)); }} aria-label="Gerundetes Ergebnis" placeholder="Ergebnis" /></div>
      </div>
      {exercise.phase === "feedback" && <div className={`answer-feedback ${exercise.feedback?.correct ? "correct" : "wrong"}`}>{exercise.feedback?.correct ? "Richtig gerundet!" : <>Nicht ganz. Richtig ist <strong>{formatNumber(exercise.feedback?.expected ?? 0)}</strong>.</>}</div>}
      {exercise.phase === "running" && <div className="mathe-actions"><button className="primary" disabled={busy || !canAnswer || exercise.value.trim() === ""} onClick={() => check().catch((error) => setMessage(error.message))}>Überprüfen</button>{canControl && <button className="secondary" disabled={busy} onClick={() => skip().catch((error) => setMessage(error.message))}>Überspringen</button>}</div>}
      {exercise.phase === "feedback" && inSyncSession && canControl && <div className="mathe-actions"><button className="primary" disabled={busy} onClick={() => advance().catch((error) => setMessage(error.message))}>{last ? "Auswertung ansehen" : "Nächste Aufgabe"}</button></div>}
    </>}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
